import { useMemo, useState } from "react";
import { formatMoney, formatPercent } from "../../api/money";
import { useAddLine, usePriceList, type PriceListPart, type QuotationDetail } from "../../api/quotations";

/** How many parts the search offers at once: enough to see the right one. */
const SHOWN = 6;

/** The parts whose code, description and size contain every word typed. */
export function findParts(parts: readonly PriceListPart[], query: string): PriceListPart[] {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) {
    return [];
  }
  return parts.filter((part) => {
    const text = `${part.code} ${part.description} ${part.size}`.toLowerCase();
    return words.every((word) => text.includes(word));
  });
}

function sized(part: PriceListPart): string {
  return part.size && part.size.toUpperCase() !== "NO SIZE" ? `${part.description} — ${part.size}` : part.description;
}

/** The quote's last row: add a line by its code off the supplier's price list.
 * Pick the code, and its description and list price come with it -- the
 * server prices the line from the list, so nobody types a list price -- then
 * the quantity. The supplier's standing discount is filled in and can be
 * changed. A line the price list does not hold (a sundry, a one-off) is still
 * added by hand, from the link under the row. */
export default function NewLineRow({ detail, columns }: { detail: QuotationDetail; columns: number }) {
  const add = useAddLine(detail.id);
  const priceList = usePriceList(detail.id, true);
  const [byHand, setByHand] = useState(false);
  const [query, setQuery] = useState("");
  const [part, setPart] = useState<PriceListPart | null>(null);
  const [qty, setQty] = useState("1");
  const [discount, setDiscount] = useState("");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState("");
  const parts = priceList.data?.parts ?? [];
  const standing = priceList.data?.discount_pct ?? detail.supplier?.discount_pct ?? "0";
  const matches = useMemo(() => (part === null && !byHand ? findParts(parts, query) : []), [parts, query, part, byHand]);

  function reset() {
    setPart(null);
    setQuery("");
    setQty("1");
    setDiscount("");
    setDescription("");
    setPrice("");
  }

  function submit() {
    const body =
      part !== null
        ? { spare_part_id: part.spare_part_id, qty, discount_pct: discount || null }
        : { description, qty, unit_price: price, discount_pct: discount || standing };
    add.mutate(body, { onSuccess: reset });
  }

  const ready = qty.trim() !== "" && (byHand ? description.trim() !== "" && price.trim() !== "" : part !== null);

  return (
    <>
      <tr className="cm-line-new">
        <td className="cm-seq muted">+</td>
        {byHand ? (
          <>
            <td />
            <td>
              <input
                className="input"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="A line the price list does not hold"
                aria-label="New line description"
              />
            </td>
          </>
        ) : part === null ? (
          <td colSpan={2}>
            <input
              type="search"
              className="input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={priceList.isPending ? "Loading the price list…" : "Search the price list by code or description"}
              aria-label="Find a part by code"
              disabled={priceList.isPending}
            />
          </td>
        ) : (
          <>
            <td className="mono">{part.code}</td>
            <td>
              {sized(part)}
              <button type="button" className="go-link cm-change-part" onClick={() => setPart(null)}>
                Change
              </button>
            </td>
          </>
        )}
        <td>
          <input value={qty} onChange={(e) => setQty(e.target.value)} aria-label="New line quantity" className="input num" />
        </td>
        <td className="num">
          {byHand ? (
            <input value={price} onChange={(e) => setPrice(e.target.value)} aria-label="New line list price" className="input num" />
          ) : part !== null ? (
            <span aria-label="New line list price">{formatMoney(part.list_price)}</span>
          ) : (
            <span className="muted">from the list</span>
          )}
        </td>
        <td>
          <input
            value={discount}
            onChange={(e) => setDiscount(e.target.value)}
            aria-label="New line discount"
            className="input num"
            placeholder={standing}
          />
        </td>
        <td />
        <td className="cm-tools">
          <button type="button" className="row-button cm-save" disabled={add.isPending || !ready} onClick={submit}>
            Add
          </button>
        </td>
      </tr>
      {matches.length > 0 || (part === null && !byHand && query.trim() !== "") ? (
        <tr className="cm-line-matches">
          <td />
          <td colSpan={columns - 1}>
            <div className="qr-matches" role="listbox" aria-label="Parts on the price list">
              {matches.length === 0 ? (
                <p className="qr-picker-hint">Nothing on {detail.supplier?.name ?? "the supplier"}&apos;s price list matches “{query.trim()}”.</p>
              ) : (
                matches.slice(0, SHOWN).map((found) => (
                  <button
                    key={found.spare_part_id}
                    type="button"
                    role="option"
                    aria-selected={false}
                    className="qr-match cm-part-match"
                    disabled={found.list_price === null}
                    onClick={() => {
                      setPart(found);
                      setQuery("");
                    }}
                  >
                    <span className="mono">{found.code}</span>
                    <span>{sized(found)}</span>
                    <span className="qr-match-price">{found.list_price === null ? "no list price" : formatMoney(found.list_price)}</span>
                  </button>
                ))
              )}
              {matches.length > SHOWN ? <p className="qr-picker-hint">{matches.length - SHOWN} more match; narrow the search to see them.</p> : null}
            </div>
          </td>
        </tr>
      ) : null}
      <tr className="cm-line-hand">
        <td />
        <td colSpan={columns - 1}>
          <button
            type="button"
            className="go-link"
            onClick={() => {
              reset();
              setByHand(!byHand);
            }}
          >
            {byHand ? "Pick a part from the price list instead" : "Not on the price list? Add a line by hand"}
          </button>
          {!byHand && part !== null ? (
            <span className="muted cm-part-terms">
              List price from the price list, less {formatPercent(discount || standing)} unless you change it.
            </span>
          ) : null}
          {add.isError ? <span className="line-warn"> {add.error.message}</span> : null}
        </td>
      </tr>
    </>
  );
}
